import { FC, useEffect } from "react";
import { addHours, format } from "date-fns";
import { uk } from "date-fns/locale/uk";
import { Typography } from "theme";

import { Box, CircularProgress, Grid2 } from "@mui/material";
import { styled } from "@mui/material/styles";

import useHalls from "../../../../api/hooks/useHalls/useHalls.ts";
import { useBooking } from "../../../../utils/context/Booking/BookingContext.tsx";

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "56px",
    width: "56px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

// Helper function to calculate day and night hours.
// Bookings can last a fractional number of hours (e.g. 2.5), so we count the
// whole hours via the loop and then allocate the remaining partial hour to the
// day/night bucket it falls into — otherwise the partial hour gets rounded up
// to a full hour and the price is overcharged.
const calculateDayNightHours = (startDate: Date, totalHours: number) => {
  let nightHours = 0;
  let dayHours = 0;

  const fullHours = Math.floor(totalHours);
  const fractionalHour = totalHours - fullHours;

  for (let i = 0; i < fullHours; i++) {
    const currentHour = addHours(startDate, i).getHours();
    if (currentHour >= 22 || currentHour < 8) {
      nightHours++;
    } else {
      dayHours++;
    }
  }

  if (fractionalHour > 0) {
    const currentHour = addHours(startDate, fullHours).getHours();
    if (currentHour >= 22 || currentHour < 8) {
      nightHours += fractionalHour;
    } else {
      dayHours += fractionalHour;
    }
  }

  return { nightHours, dayHours };
};

const SelectedHall:FC<{
  setHallPrice: (value: number) => void;
}> = ({setHallPrice}) => {
  const { booking } = useBooking();
  const { halls, isFetching } = useHalls();

  const selectedHall = halls?.find((h) => h.id === booking?.hall);

  useEffect(() => {
    if (selectedHall && booking){
      const startDate = new Date(booking.slotDate.includes('Z') ? booking.slotDate.slice(0, -1) : booking.slotDate);
      const { nightHours, dayHours } = calculateDayNightHours(startDate, booking.hoursNumber);
      const calculatedPrice = (dayHours * selectedHall.pricePerHour) + (nightHours * selectedHall.nightPricePerHour);
      setHallPrice(calculatedPrice);
    }
  }, [selectedHall, booking, setHallPrice]);

  if (isFetching) {
    return (
      <Grid2 container justifyContent="center">
        <CircularProgress color="secondary" />
      </Grid2>
    );
  }

  if (!selectedHall || !booking) return null;

  const startDate = new Date(booking.slotDate.includes('Z') ? booking.slotDate.slice(0, -1) : booking.slotDate);
  const endDate = addHours(startDate, booking.hoursNumber);
  const { nightHours, dayHours } = calculateDayNightHours(startDate, booking.hoursNumber);
  const totalPrice = (dayHours * selectedHall.pricePerHour) + (nightHours * selectedHall.nightPricePerHour);

  return (
    <Box>
      <Grid2 mb={1.25} container justifyContent='space-between' alignItems='center'>
        <Typography variant="body1" weight="regular">
          Вартість кімнати:
        </Typography>
        <Typography variant="body1" weight="semiBold">
          {totalPrice} грн.
        </Typography>
      </Grid2>
    <Grid2 container columnGap={1.5} mb={1.5}>
      <Box>
        <StyledImage src={selectedHall.imageUrl} />
      </Box>
      <Box>
        <Typography mb={0.5} variant="body1" weight="semiBold" textTransform="uppercase">
          {selectedHall.name}
        </Typography>

        <Grid2 container columnGap={1.5} alignItems='center'>
          <Typography my={0.5} variant="body1" weight="regular">
            Тривалість: {booking.hoursNumber} год
          </Typography>

          {dayHours > 0 && nightHours > 0 ? (
            <Typography variant="body1" weight="semiBold" textTransform="uppercase">
              {dayHours} год × {selectedHall.pricePerHour} грн + {nightHours} год × {selectedHall.nightPricePerHour} грн
            </Typography>
          ) : nightHours > 0 ? (
            <Typography variant="body1" weight="semiBold" textTransform="uppercase">
              {selectedHall.nightPricePerHour} грн/год (нічна)
            </Typography>
          ) : (
            <Typography variant="body1" weight="semiBold" textTransform="uppercase">
              {selectedHall.pricePerHour} грн/год
            </Typography>
          )}
        </Grid2>

        <Typography my={0.5} variant="body1" weight="regular">
          Дата: {`${format(startDate, "EEEE, dd MMMM HH:mm", {
          locale: uk,
        })} - ${format(endDate, "HH:mm")}`}
        </Typography>
      </Box>
    </Grid2>
    </Box>
  );
};

export default SelectedHall;
